import React, { useState } from "react";
import {
  Box,
  Button,
  FormField,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { Investigation, Stage } from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useReleaseInvestigation,
  useTransferInvestigation,
} from "../../hooks/useInvestigations";
import { normaliseAlias } from "./AssigneeAutosuggest";
import { InvestigationStaleBadge } from "./InvestigationStaleBadge";
import { NeedsOwnerBadge } from "./NeedsOwnerBadge";
import { isTerminal } from "./investigationUtils";
import { OwnerSelect } from "./OwnerSelect";

export { avatarUrl } from "./UserAvatar";

/**
 * Owner picker in the style of Taskei's Assignee field: the current owner with their photo in a
 * searchable dropdown, "Assign to me" and "Unassign" beside it. Choosing someone applies at once;
 * only taking a case away from another person asks first. The backend rules are unchanged: anyone
 * may reassign (to a Lusca Search user), only the owner may unassign, closed cases are locked.
 */
export const InvestigationOwnerField: React.FC<{
  stage: Stage;
  investigation: Investigation;
  currentUser: string | null;
  onError: (message: string) => void;
}> = ({ stage, investigation, currentUser, onError }) => {
  const transfer = useTransferInvestigation(stage);
  const release = useReleaseInvestigation(stage);
  const [pending, setPending] = useState<string | null>(null);
  const owner = investigation.owner || null;
  const me = currentUser ? normaliseAlias(currentUser) : null;
  const isOwner = !!me && owner === me;
  const locked = isTerminal(investigation.status);
  const busy = transfer.isLoading || release.isLoading;

  const contributors = (investigation.contributors || []).filter(
    (c) => c !== owner
  );
  const pinned = [
    ...(me && me !== owner ? [{ alias: me, description: "Me" }] : []),
    ...(owner ? [{ alias: owner, description: "Current owner" }] : []),
    ...contributors
      .filter((c) => c !== me)
      .map((c) => ({ alias: c, description: "Contributor" })),
  ];

  let description: string | undefined;
  if (locked) description = "Reopen the investigation to change its owner.";
  else if (contributors.length)
    description = `Contributors: ${contributors.join(", ")}`;

  const assign = (alias: string) =>
    transfer
      .mutateAsync({
        investigationId: investigation.id,
        request: { expectedVersion: investigation.version, newOwner: alias },
      })
      .then(() => setPending(null))
      .catch((err: InvestigationApiError) => onError(err.message));

  const choose = (alias: string) => {
    if (!alias || alias === owner) return;
    // Taking a live case away from someone else is the one change worth a confirmation.
    if (owner && !isOwner) setPending(alias);
    else assign(alias).catch(console.error);
  };

  const unassign = () =>
    release
      .mutateAsync({
        investigationId: investigation.id,
        expectedVersion: investigation.version,
      })
      .catch((err: InvestigationApiError) => onError(err.message));

  return (
    <FormField
      label={
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <span>Owner</span>
          {!owner && <NeedsOwnerBadge />}
          <InvestigationStaleBadge investigation={investigation} />
        </SpaceBetween>
      }
      description={description}
    >
      <div
        style={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1 1 180px", minWidth: 0 }}>
          <OwnerSelect
            stage={stage}
            value={owner}
            pinned={pinned}
            disabled={locked || busy}
            onChange={choose}
          />
        </div>
        {!locked && me && !isOwner && (
          <Button onClick={() => choose(me)} loading={transfer.isLoading}>
            Assign to me
          </Button>
        )}
        {!locked && isOwner && (
          <Button
            onClick={() => {
              unassign().catch(console.error);
            }}
            loading={release.isLoading}
          >
            Unassign
          </Button>
        )}
      </div>
      <Modal
        visible={!!pending}
        onDismiss={() => setPending(null)}
        header="Reassign this investigation?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={transfer.isLoading}
                onClick={() => {
                  if (pending) assign(pending).catch(console.error);
                }}
              >
                Reassign
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {owner} owns this investigation. Reassign it to{" "}
        <strong>{pending === me ? "you" : pending}</strong>? {owner} is notified
        and stays on the case as a contributor.
      </Modal>
    </FormField>
  );
};

export default InvestigationOwnerField;
